import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../utils/axios';

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminBannerVisible, setAdminBannerVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        const response = await apiClient.get('/iam/me');
        const userData = response.data;
        setUser(userData);
        const hasAdminRole = userData.role === 'admin';
        const isAdminTenant = userData.tenant_code === 'admin';
        setIsAdmin(hasAdminRole || isAdminTenant);
      } catch (err) {
        setUser(null);
        setIsAdmin(false);
      }

      try {
        const response = await apiClient.get('/iam/me');

        const userData = response.data;
        setUser(userData);
        
        // Check if user has admin access
        const hasAdminRole = userData.role === 'admin';
        const isAdminTenant = userData.tenant_code === 'admin';
        setIsAdmin(hasAdminRole || isAdminTenant);
      } catch (err) {
        console.error('Auth check failed:', err);
        setUser(null);
        setIsAdmin(false);
      }
    };

    checkUserAuth();
  }, []);

  // Check if admin banner is visible by listening to custom events
  useEffect(() => {
    const handleBannerVisibilityChange = (event) => {
      setAdminBannerVisible(event.detail.visible);
    };

    window.addEventListener('adminBannerVisibilityChanged', handleBannerVisibilityChange);
    
    return () => {
      window.removeEventListener('adminBannerVisibilityChanged', handleBannerVisibilityChange);
    };
  }, []);

  // Reset banner visibility when user is no longer admin
  useEffect(() => {
    if (!isAdmin) {
      setAdminBannerVisible(false);
    }
  }, [isAdmin]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Don't render navigation if user is not signed in
  if (!user) {
    return null;
  }

  // Styles matching dashboard design system
  const sidebarStyle = {
    position: 'fixed',
    left: 0,
    width: isMobile ? '100%' : '200px',
    backgroundColor: 'var(--bg-primary)',
    borderRight: isMobile ? 'none' : '1px solid var(--border-color)',
    borderBottom: isMobile ? '1px solid var(--border-color)' : 'none',
    boxShadow: isMobile ? '0 2px 8px var(--shadow-light)' : '2px 0 8px var(--shadow-light)',
    zIndex: 1000,
    transition: 'var(--transition-standard)',
    transform: isMobile ? (isMobileMenuOpen ? 'translateY(0)' : 'translateY(-100%)') : 'translateX(0)',
    overflowY: 'auto',
    padding: '0',
    // For mobile: position below the header
    top: isMobile ? (isAdmin && adminBannerVisible ? 'calc(2.75rem + 60px)' : '60px') : (isAdmin && adminBannerVisible ? '2.75rem' : '0'),
    height: isMobile 
      ? (isMobileMenuOpen 
          ? (isAdmin && adminBannerVisible ? 'calc(100vh - 2.75rem - 60px)' : 'calc(100vh - 60px)')
          : '0'
        )
      : (isAdmin && adminBannerVisible ? 'calc(100vh - 2.75rem)' : '100vh'),
    display: 'flex',
    flexDirection: 'column'
  };

  const mobileHeaderStyle = {
    position: 'fixed',
    top: isAdmin && adminBannerVisible ? '2.75rem' : '0',
    left: 0,
    right: 0,
    height: '60px',
    backgroundColor: 'var(--bg-primary)',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1rem',
    boxShadow: '0 2px 4px var(--shadow-light)',
    zIndex: 1001,
    transition: 'var(--transition-standard)'
  };

  const logoStyle = {
    color: 'var(--text-primary)',
    fontSize: '1.5rem',
    fontWeight: '600',
    textDecoration: 'none',
    padding: '1.5rem 1rem',
    borderBottom: '1px solid var(--border-color)',
    display: 'block',
    marginBottom: '1rem',
    transition: 'var(--transition-standard)',
    background: 'var(--bg-secondary)'
  };

  const hamburgerStyle = {
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.5rem',
    transition: 'var(--transition-standard)',
    borderRadius: '6px',
    transform: isMobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)'
  };

  const navContentStyle = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column'
  };

  const navSectionStyle = {
    padding: '0 1rem'
  };

  const sectionTitleStyle = {
    color: 'var(--text-muted)',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '1.5rem 0 0.75rem 0',
    padding: '0 0.5rem'
  };

  const navListStyle = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const navItemStyle = {
    margin: 0
  };

  // Navigation item styled like dashboard admin-tool-item
  const linkStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    background: 'var(--bg-secondary)',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'var(--transition-standard)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    fontWeight: '500'
  };

  const activeLinkStyle = {
    ...linkStyle,
    background: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
  };

  const arrowStyle = {
    marginLeft: 'auto',
    color: 'var(--color-primary)',
    fontSize: '0.875rem',
    fontWeight: 'bold'
  };

  const activeArrowStyle = {
    ...arrowStyle,
    color: 'white'
  };

  const logoutButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.375rem',
    background: 'transparent',
    borderRadius: '4px',
    border: '1px solid var(--border-color)',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '400',
    transition: 'var(--transition-standard)',
    flexShrink: 0,
    width: '2rem',
    height: '2rem'
  };

  const bottomSectionStyle = {
    marginTop: 'auto',
    padding: '1rem',
    borderTop: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)'
  };

  const userInfoStyle = {
    padding: '0.75rem',
    background: 'var(--bg-tertiary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const userNameStyle = {
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    fontWeight: '600',
    margin: '0 0 0.25rem 0'
  };

  const userRoleStyle = {
    color: 'var(--text-muted)',
    fontSize: '0.75rem',
    margin: '0'
  };

  const isActivePath = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const renderNavItems = () => {
    const mainNavItems = [
      //{ path: '/timeline', label: 'Timeline Graph' },
      { path: '/contacts', label: 'Contacts' },
      //{ path: '/timeline-list', label: 'Timeline List' },
      { path: '/analysis', label: 'Insights' },
      { path: '/lifecycles', label: 'Lifecycles' },
    ];

    const adminNavItems = isAdmin ? [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/integrations', label: 'Integrations' },
      ...(user && user.role === 'admin' && user.tenant_code !== 'admin' ? [
        { path: '/mcp-tokens', label: 'API Tokens' },
      ] : []),
      ...(user && user.role === 'admin' && user.tenant_code === 'admin' ? [
        { path: '/admin/super', label: 'Super Admin' },
        { path: '/admin/reprocessing', label: 'Reprocessing' },
      ] : [])
    ] : [];

    return (
      <div style={navSectionStyle}>
        {/* Main Navigation */}
        <div style={sectionTitleStyle}>Navigation</div>
        <ul style={navListStyle}>
          {mainNavItems.map(item => (
            <li key={item.path} style={navItemStyle}>
              <Link
                to={item.path}
                style={isActivePath(item.path) ? activeLinkStyle : linkStyle}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={isActivePath(item.path) ? activeArrowStyle : arrowStyle}>→</span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Admin Navigation */}
        {adminNavItems.length > 0 && (
          <>
            <div style={sectionTitleStyle}>Administration</div>
            <ul style={navListStyle}>
              {adminNavItems.map(item => (
                <li key={item.path} style={navItemStyle}>
                  <Link
                    to={item.path}
                    style={isActivePath(item.path) ? activeLinkStyle : linkStyle}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <span style={isActivePath(item.path) ? activeArrowStyle : arrowStyle}>→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <header style={mobileHeaderStyle}>
          <Link to="/" style={{ color: 'var(--text-primary)', textDecoration: 'none', fontSize: '1.2rem', fontWeight: '600' }}>
            TouchFlow
          </Link>
          <button
            style={hamburgerStyle}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--bg-tertiary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
            }}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>
        </header>
      )}

      {/* Navigation Panel */}
      <nav style={sidebarStyle}>
        {!isMobile && (
          <Link 
            to="/" 
            style={logoStyle}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--bg-tertiary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'var(--bg-secondary)';
            }}
          >
            TouchFlow
          </Link>
        )}
        
        <div style={navContentStyle}>
          {renderNavItems()}
        </div>

        {/* Bottom Section with User Info and Logout */}
        <div style={bottomSectionStyle}>
          <Link
            to="/docs/mcp-setup"
            style={{
              display: 'block',
              color: 'var(--text-muted)',
              fontSize: '0.8rem',
              textDecoration: 'none',
              marginBottom: '0.75rem',
              padding: '0 0.25rem',
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Docs
          </Link>
          <div style={userInfoStyle}>
            <div style={{ flex: 1 }}>
              <div style={userNameStyle}>
                {user.first_name || user.email}
              </div>
              <div style={userRoleStyle}>
                {user.role === 'admin' ? 'Administrator' : 'User'} • {user.tenant_code}
              </div>
            </div>
            <button
              style={logoutButtonStyle}
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--bg-secondary)';
                e.target.style.color = 'var(--text-primary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'var(--text-muted)';
              }}
              title="Logout"
            >
              ⌐
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navigation; 